/* eslint sort-keys: 0 */
import {
  LIGHT_BLUE_RGBA,
  PAYPAL_OPEN_FONT,
  PP_ACCENT_ONE_COLOR,
  PP_ACTIVE_BG_COLOR,
  PP_BORDER_HIGH_CONTRAST_COLOR,
  PP_ERROR_COLOR,
  PP_ERROR_OUTLINE_COLOR,
  PP_FOCUS_BORDER_COLOR,
  PP_FOCUS_OUTLINE_COLOR,
  PP_LIGHT_GRAY_COLOR,
  PP_LINK_MAIN_COLOR,
  PP_PRIMARY_COLOR,
  PP_SECONDARY_COLOR,
  PP_SELECTED_COLOR,
  PP_SYSTEM_COLOR,
  PP_TERTIARY_COLOR,
  WHITE_COLOR,
} from '../styleVariables';

export const ppReactTheme: AppTheme.WeasleyTheme = {
  button: {
    closeButton: {
      borderRadius: '50%',
      color: PP_LIGHT_GRAY_COLOR,
      height: '48px',
      padding: '12px',
      width: '48px',
      hover: {
        color: PP_LIGHT_GRAY_COLOR,
        background: PP_TERTIARY_COLOR,
      },
      focus: {
        background: 'transparent',
        border: `1px solid ${PP_SELECTED_COLOR}`,
        boxShadow: `${PP_SECONDARY_COLOR} 0px 0px 0px 2px inset, ${LIGHT_BLUE_RGBA} 0px 0px 0px 5px`,
        borderRadius: '50%',
      },
    },
    active: {
      background: PP_ACTIVE_BG_COLOR,
      border: `1px solid ${PP_ACTIVE_BG_COLOR}`,
    },
    default: {
      background: PP_PRIMARY_COLOR,
      border: `1px solid ${PP_PRIMARY_COLOR}`,
      color: WHITE_COLOR,
      font_size: '18px',
      text_decoration: 'none',
      weight: 700,
    },
    focus: {
      background: PP_PRIMARY_COLOR,
      border: `3px solid ${PP_FOCUS_BORDER_COLOR}`,
      box_shadow: `${PP_FOCUS_OUTLINE_COLOR} 0px 0px 0px 6px`,
      text_decoration: 'none',
    },
    hover: {
      background: PP_SECONDARY_COLOR,
      border: `1px solid ${PP_SECONDARY_COLOR}`,
      text_decoration: 'unset',
    },
    disabled: {
      color: PP_BORDER_HIGH_CONTRAST_COLOR,
    },
    variant: {
      outlined: {
        active: {
          background: 'transparent',
          border: `2px solid ${PP_ACTIVE_BG_COLOR}`,
          color: PP_ACTIVE_BG_COLOR,
        },
        default: {
          background: 'transparent',
          border: `2px solid ${PP_PRIMARY_COLOR}`,
          color: PP_PRIMARY_COLOR,
        },
        focus: {
          background: 'transparent',
          border: `3px solid ${PP_SECONDARY_COLOR}`,
          box_shadow: `${LIGHT_BLUE_RGBA} 0px 0px 0px 0.375rem`,
          color: PP_PRIMARY_COLOR,
          text_decoration: 'unset',
        },
        hover: {
          background: 'transparent',
          border: `2px solid ${PP_SECONDARY_COLOR}`,
          color: PP_SECONDARY_COLOR,
          text_decoration: 'unset',
        },
      },
      text: {
        default: {
          background: 'transparent',
          border: 'none',
          color: PP_LINK_MAIN_COLOR,
          fontSize: '18px',
        },
        focus: {
          background: 'transparent',
          border: `3px solid ${PP_LINK_MAIN_COLOR}`,
          border_radius: '4px',
          box_shadow: `${LIGHT_BLUE_RGBA} 0px 0px 0px 6px`,
          color: PP_LINK_MAIN_COLOR,
          text_decoration: 'underline',
        },
        hover: {
          background: 'transparent',
          border: 'none',
          color: PP_LINK_MAIN_COLOR,
          text_decoration: 'underline',
        },
      },
    },
  },
  input: {
    default: {
      color: PP_SYSTEM_COLOR,
      border: `1px solid ${PP_BORDER_HIGH_CONTRAST_COLOR}`,
      boxShadow: 'none',
    },
    focus: {
      color: PP_SYSTEM_COLOR,
      border: `1px solid ${PP_SECONDARY_COLOR}`,
      boxShadow: `${PP_SECONDARY_COLOR} 0px 0px 0px 2px inset, ${LIGHT_BLUE_RGBA} 0px 0px 0px 5px`,
    },
    error: {
      default: {
        border: `1px solid ${PP_ERROR_COLOR}`,
        boxShadow: 'none',
      },
      focus: {
        border: `2px solid ${PP_ERROR_COLOR}`,
        boxShadow: `${PP_ERROR_COLOR} 0px 0px 0px 1px inset, ${PP_ERROR_OUTLINE_COLOR} 0px 0px 0px 5px`,
      },
    },
  },
  checkbox: {
    container: {
      default: { paddingBottom: '16px' },
      terms: { paddingBottom: '16px' },
    },
    input: {
      checked: {
        focus: {
          labelBefore: {
            background: PP_SECONDARY_COLOR,
            border: `1px solid ${PP_SECONDARY_COLOR}`,
            borderRadius: '4px',
            boxShadow: `${LIGHT_BLUE_RGBA} 0 0 0 6px`,
          },
        },
        noFocus: {
          labelBefore: {
            background: PP_SECONDARY_COLOR,
            border: `1px solid ${PP_SECONDARY_COLOR}`,
            borderRadius: '4px',
          },
        },
      },
      noChecked: {
        focus: {
          labelBefore: {
            border: `1px solid ${PP_BORDER_HIGH_CONTRAST_COLOR}`,
            borderRadius: '4px',
            boxShadow: `${LIGHT_BLUE_RGBA} 0 0 0 6px`,
          },
        },
      },
    },
    label: {
      after: { top: '7px' },
      before: {
        border: `1px solid ${PP_BORDER_HIGH_CONTRAST_COLOR}`,
        borderRadius: '4px',
        top: '3px',
      },
      default: {
        color: PP_SYSTEM_COLOR,
        fontSize: '18px',
        fontWeight: 400,
        lineHeight: '24px',
      },
      terms: {
        color: PP_LIGHT_GRAY_COLOR,
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '20px',
      },
    },
  },
  link: {
    default: {
      color: PP_LINK_MAIN_COLOR,
      lineHeight: '20px',
    },
    hover: { color: PP_ACCENT_ONE_COLOR },
    focus: {
      borderRadius: '4px',
      boxShadow: `${PP_SECONDARY_COLOR} 0 0 0 0.1875rem, rgb(16 114 235 / 16%) 0 0 0 0.375rem`,
      outline: 'transparent solid 0.1875rem',
    },
    legalLink: {
      color: PP_LIGHT_GRAY_COLOR,
      fontWeight: '400',
      fontSize: '14px',
    },
  },
  themeType: 'ppReactTheme',
  loadingSkeleton: {
    height: '64px',
    viewBoxHeight: '64',
  },
  typography: {
    caption: { color: PP_SYSTEM_COLOR },
    caption_light: { color: PP_LIGHT_GRAY_COLOR },
    font: { family: PAYPAL_OPEN_FONT },
    headingH1: { color: PP_SYSTEM_COLOR },
    headingH2: { color: PP_SYSTEM_COLOR },
    textHelper: { color: PP_LIGHT_GRAY_COLOR },
    body: { color: PP_SYSTEM_COLOR },
    fontSize: {
      xLarge: '28px',
      large: '24px',
      medium: '22px',
      regular: '18px',
      small: '14px',
    },
    lineHeight: {
      xLarge: '36px',
      large: '24px',
      medium: '20px',
    },
    weight: {
      xBold: 700,
      bold: 500,
      regular: 400,
    },
  },
  soldoutTag: {
    border: `2px solid ${PP_ERROR_COLOR}`,
    borderColor: `transparent transparent ${PP_ERROR_COLOR} ${PP_ERROR_COLOR}`,
    color: PP_ERROR_COLOR,
  },
  wrappers: {
    content: {
      maxWidth: '400px',
    },
  },
  WHITE_COLOR,
  RED_COLOR: PP_ERROR_COLOR,
};
